import { MouseEvent, useEffect, useState } from "react";

import PopupHeader from "../../../components/Popups/PopupHeader";
import PopupFooter from "../../../components/Popups/PopupFooter";
import { Btn } from "../../../components/Buttons/Buttons";
import { Dataset } from "../../../api/models";
import { useGetDatasetsDatasetGet } from "../../../api/generated/fastAPI";
import SuccessMessageModal from "../../../components/Popups/SuccessMessageModal";
import usePost from "../../../hooks/endpoints/usePost";
import ModalBase from "../../../components/Popups/ModalBase";
import Curate, { CurationTypes, EcleanCuration } from "./GenerateDSComponents/Curate";


const defaultUpdatedCurationForm: EcleanCuration = {
    eclean_std_smiles: {
        original_smiles_column: '',
        remove_stereo: false,
    },
    eclean_std_binary_values: null,
    eclean_std_values: null,
    eclean_std_binary: null,
    eclean_std_regression: null,
    input_units: null,
}

export interface CurationForm {
    eclean_std_smiles: {
        original_smiles_column: string,
        remove_stereo: boolean,
        standardize_tautomers: boolean,
    },
    eclean_std_binary_values: {
        binary_value_column: string,
    } | null,
    eclean_std_values: {
        units_column: string,
        units_out: string,
    } | null,
    eclean_std_binary: {
        binarization_threshold: number,
        binarization_qualifier: string,
    } | null,
}

export default function CurateDatasetModal({
    selectedDatasetId,
    setPopupMessage,
    handleCloseMenu,
}: {
    selectedDatasetId: string,
    setPopupMessage : (popupMessage: string, isSuccessMessage: boolean) => void,
    handleCloseMenu?: () => void,
}) {
    const [open, setOpen] = useState(false);
    const handleOpen = () => setOpen(true);
    const [ showSelect, setShowSelect ] = useState(false);
    

    const [ formBtns, setFormBtns ] = useState<Btn[]>([]);
    const [ isDisabled, setIsDisabled ] = useState(false);
    const [ isLoading, setIsLoading ] = useState(false);
    const [ fieldOptions, setFieldOptions ] = useState<{ value: string, label: string }[]>([]);
    const [ selectedDataset, setSelectedDataset ] = useState<Dataset | null>(null);
    const getDsMutation = useGetDatasetsDatasetGet({ dataset_hash: selectedDatasetId ? selectedDatasetId : '' });


    const [ curationForm, setCurationForm ] = useState<EcleanCuration>(defaultUpdatedCurationForm);
    const [ activityColumns, setActivityColumns ] = useState<{
        column: string,
        data_type: string,
    }[]>([]);
    const [ stdSMILES, setStdSMILES ] = useState<string | null>(null);
    const [ curationType, setCurationType ] = useState<CurationTypes>('smiles');

    const [ isSuccess, setIsSuccess ] = useState(false);

    const handleClose = () => {
        setOpen(false);
        resetValues();
        setShowSelect(false);
    };

    const resetValues = () => {
        setFieldOptions([]);
        setIsDisabled(false);
        setIsLoading(false);
        setIsSuccess(false);
        setCurationForm(defaultUpdatedCurationForm);

        handleCloseMenu && setTimeout(() => handleCloseMenu(), 0);
    };

    const { handleCurateDataset } = usePost({
        setIsDisabled,
        setIsLoading,
        setPopupMessage,
        setIsSuccess,
    });


    useEffect(() => {
            setFormBtns([
                {
                    type: "tertiary",
                    styling: "outline",
                    id: 'cancel-file-upload',
                    label: 'Cancel',
                    action: (e: MouseEvent<HTMLButtonElement>) => {handleClose()},
                },
                {
                    type: isDisabled ? 'disabled' : 'primary',
                    styling: "solid",
                    id: 'run-data-curation',
                    label: "Run",
                    action: (e: MouseEvent<HTMLButtonElement>) => handleCurateDataset(e, selectedDataset, curationForm, curationType),
                    isLoading,
                }
    
            ])
            //eslint-disable-next-line
        }, [isDisabled, isLoading, selectedDataset, curationForm, curationType]);

    
    useEffect(() => {
        if (getDsMutation.data) {
            const datasets = getDsMutation.data as unknown as Dataset[];

            if (datasets.length > 0) {
                const matchingDs = datasets.find(ds => ds.file_hash! === selectedDatasetId);

                if (matchingDs) {
                    setSelectedDataset(matchingDs);
                }
            }
        }
    }, [getDsMutation.data, selectedDatasetId]);

    useEffect(() => {
        if (getDsMutation.data) {
            const ds_metadata = getDsMutation.data as unknown as Dataset;
            const columnMetadata = ds_metadata.dataset_metadata!.column_metadata;
            const stages = ds_metadata.dataset_metadata!.stages;

            if (columnMetadata) {
                const columnKeys = Object.keys(columnMetadata);

                let activity: { column: string, data_type: string }[] = [];

                columnKeys.forEach(key => {
                    const columnVal = columnMetadata[key];
                    if (columnVal && columnVal.data_type) {
                        const data_type = columnVal.data_type;
                        if (['single-class-label', 'continuous-value'].includes(data_type)) {
                            activity.push({ column: key, data_type })
                        }
                    }
                });

                setActivityColumns(activity);

                setFieldOptions(columnKeys.map(key => ({ value: key, label: key })));


            }

            if (stages && stages.length > 0) {
                let smilesValue: string | null = null;

                const stdSmilesStage = stages.find(stage => stage.function && stage.function === 'eclean_std_smiles');
                if (stdSmilesStage && stdSmilesStage.function_params && Object.keys(stdSmilesStage.function_params).length > 0) {
                    const functionParams = stdSmilesStage.function_params;
                    const stdSmilesColumnKey = Object.keys(functionParams).find(paramKey => paramKey === 'std_smiles_column');
                    if (stdSmilesColumnKey && functionParams[stdSmilesColumnKey]) {
                        smilesValue = functionParams[stdSmilesColumnKey] as string;
                    }
                };

                setStdSMILES(smilesValue);
            }

        }
    }, [getDsMutation.data]);

    useEffect(() => {
        const { 
            eclean_std_smiles,
            eclean_std_binary_values,  
            eclean_std_values,
            eclean_std_binary, 
            eclean_std_regression,
            input_units
        } = curationForm;

        let disabled = false;

        if (eclean_std_smiles !== null) {
            if (!eclean_std_smiles.original_smiles_column) {
               disabled = true;
            }
        } else if (!stdSMILES) {
            disabled = true;
        } else {
            if (eclean_std_binary_values !== null) {
                if (!eclean_std_binary_values.binary_value_column) {
                    disabled = true;
                }
            }

            if (eclean_std_values !== null) {
                if (!eclean_std_values.units_out || !eclean_std_values.value_column) {
                    disabled = true;
                } else if (!eclean_std_values.units_column && !input_units) {
                    disabled = true;
                }
            }

            if (eclean_std_binary !== null) {
                if (eclean_std_binary.binarization_threshold === null) {
                    if (!eclean_std_binary.binary_value_column) {
                        disabled = true;
                    }
                } else {
                    if (!eclean_std_binary.binarization_threshold_unit_conversion!.binarization_units || !eclean_std_binary.binarization_threshold_unit_conversion!.units_out || !eclean_std_binary.value_column) {
                        disabled = true;
                    }
                }
            } 

            if (eclean_std_regression !== null) {
                if (!eclean_std_regression.value_column) {
                    disabled = true;
                }
            }
        };

        setIsDisabled(disabled);

    }, [curationForm, stdSMILES]);


    return (
        <div className="w-full">
            <button onClick={handleOpen} className={`w-full px-5 py-3 text-left font-medium flex justify-between items-center gap-3 w-full h-full`}>
                <div className='flex justify-start items-center gap-3'>
                    <p className='text-nowrap'>Curate Dataset</p>
                </div>
            </button>
            <ModalBase
                open={open}
                setShowSelect={setShowSelect}
                modalId="curate-ds-popup"
            >
            {!isSuccess ? (
                <div className={`bg-white rounded-lg max-w-[600px] w-full popup-max-height mx-auto`}>
                    <div className='flex flex-col justify-center items-stretch popup-max-height'> 
                        <PopupHeader
                            label="Curate Dataset"
                            hasOutline={true}
                            closeFn={handleClose}
                        />  
                        <div className="overflow-y-auto">
                            <div className={`w-full p-6 flex flex-col gap-8 justify-start items-stretch min-h-[600px]`}>
                                <Curate 
                                    form={curationForm}
                                    setForm={setCurationForm}
                                    isCurationTrue={true}
                                    curationType={curationType}
                                    setCurationType={setCurationType}
                                    fieldOptions={fieldOptions}
                                    showSelect={showSelect}
                                    isLoading={getDsMutation.isLoading}
                                    menuPortalTargetId="curate-ds-popup"
                                    activityColumns={activityColumns}
                                    stdSMILES={stdSMILES}
                                    originalSMILES={''}
                                    isSDF={false}
                                />
                            </div>
                        </div> 
                        <PopupFooter hasOutline={true} buttons={formBtns}/>
                    </div>
                </div>
            ) : (
                <SuccessMessageModal 
                    handleClose={handleClose}
                    message="Your dataset is now being curated. You can check its progress in the Jobs Queue. Once the job is complete, the results will appears in the Datasets page."
                />
            )}
            </ModalBase>
        </div>
    )
}